export const DEFAULT_FOCUS_PREFERENCES = {
  version: 3,
  defaultPreset: 'all',
  defaultSort: 'lastMovementAt_desc',
  customPresets: [],
  hiddenStatuses: ['Concluida', 'Cancelada', 'Finalizada', 'Encerrada', 'Concluido', 'Cancelado', 'Finalizado', 'Encerrado'],
  hiddenStages: ['Concluido', 'Finalizado', 'Encerrado'],
  focusCardLimit: 5,
  isolateOnOpen: true,
  staleHours: {
    level1: 12,
    level2: 24,
    level3: 36
  }
};

function uniqueStrings(values) {
  return [...new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )];
}

function slugify(value, fallback = 'foco') {
  const base = String(value || fallback)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 36);

  return base || fallback;
}

function normalizeHour(value, fallback) {
  const safeValue = Number(value);
  if (!Number.isFinite(safeValue)) return fallback;
  return Math.min(Math.max(Math.round(safeValue), 1), 240);
}

function readHiddenValues(source, key, fallback) {
  if (!source || typeof source !== 'object' || !(key in source)) return [...fallback];

  const values = uniqueStrings(source[key]);
  const version = Number(source?.version || 0);
  if (!values.length && version < DEFAULT_FOCUS_PREFERENCES.version) {
    return [...fallback];
  }

  return values;
}

function normalizeCustomPreset(value = {}, index = 0) {
  const label = String(value?.label || '').trim().slice(0, 32);
  const id = String(value?.id || `custom_${slugify(label, `foco_${index + 1}`)}`).trim();
  const assigneeMode = ['any', 'me', 'unassigned'].includes(value?.assigneeMode)
    ? value.assigneeMode
    : 'any';

  if (!label) return null;

  return {
    id: id.startsWith('custom_') ? id : `custom_${slugify(id)}`,
    label,
    q: String(value?.q || '').trim().slice(0, 80),
    statuses: uniqueStrings(value?.statuses).slice(0, 20),
    stages: uniqueStrings(value?.stages).slice(0, 20),
    priorities: uniqueStrings(value?.priorities).slice(0, 20),
    areas: uniqueStrings(value?.areas).slice(0, 20),
    assigneeMode,
    attentionOnly: Boolean(value?.attentionOnly)
  };
}

function normalizeCustomPresets(values) {
  const presets = (Array.isArray(values) ? values : [])
    .map(normalizeCustomPreset)
    .filter(Boolean)
    .slice(0, 10);
  const seen = new Set();

  return presets.map((preset, index) => {
    let id = preset.id || `custom_foco_${index + 1}`;
    while (seen.has(id)) id = `${preset.id}_${index + 1}`;
    seen.add(id);
    return { ...preset, id };
  });
}

export function normalizeFocusPreferences(value = {}) {
  const staleLevel1 = normalizeHour(value?.staleHours?.level1, DEFAULT_FOCUS_PREFERENCES.staleHours.level1);
  const staleLevel2 = Math.max(
    normalizeHour(value?.staleHours?.level2, DEFAULT_FOCUS_PREFERENCES.staleHours.level2),
    staleLevel1 + 1
  );
  const staleLevel3 = Math.max(
    normalizeHour(value?.staleHours?.level3, DEFAULT_FOCUS_PREFERENCES.staleHours.level3),
    staleLevel2 + 1
  );

  return {
    version: DEFAULT_FOCUS_PREFERENCES.version,
    defaultPreset: String(value?.defaultPreset || DEFAULT_FOCUS_PREFERENCES.defaultPreset),
    defaultSort: String(value?.defaultSort || DEFAULT_FOCUS_PREFERENCES.defaultSort),
    customPresets: normalizeCustomPresets(value?.customPresets),
    hiddenStatuses: readHiddenValues(value, 'hiddenStatuses', DEFAULT_FOCUS_PREFERENCES.hiddenStatuses),
    hiddenStages: readHiddenValues(value, 'hiddenStages', DEFAULT_FOCUS_PREFERENCES.hiddenStages),
    focusCardLimit: Math.min(Math.max(Number(value?.focusCardLimit) || DEFAULT_FOCUS_PREFERENCES.focusCardLimit, 1), 12),
    isolateOnOpen: typeof value?.isolateOnOpen === 'boolean'
      ? value.isolateOnOpen
      : DEFAULT_FOCUS_PREFERENCES.isolateOnOpen,
    staleHours: {
      level1: staleLevel1,
      level2: staleLevel2,
      level3: staleLevel3
    }
  };
}
